import { AppTitle } from '@/components/atoms/AppTitle/AppTitle'
import { useLogin } from '@/features/Auth/auth.hooks'
import { createUserProfileQueryOptions } from '@/features/User/queries'
import { AppRoutes } from '@/routes'
import { authService, profileService } from '@/services'
import { parseError } from '@/shared/helpers/error'
import { formatSolanaPublicKey } from '@/shared/helpers/string'
import { trackUserAction } from '@/shared/helpers/sentry'
import { useAppWallet } from '@/shared/wallet/useAppWallet'
import { useAuthStore } from '@/store'
import * as Sentry from '@sentry/nextjs'
import { useOtpStore } from '@/store/otp/otp.store'
import { Button, Flex, message } from 'antd'
import { AxiosError } from 'axios'
import { useRouter } from 'nextjs-toploader/app'
import React, { useCallback, useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import styles from './AuthWalletConnected.module.scss'
import { useQuery } from '@tanstack/react-query'
import { createBalancesQueryOptions } from '@/features/Wallets/queries'
import { AuthStep } from '@/features/Auth/auth.types'
import { AuthLoader } from '@/features/Auth/ui/AuthLoader/AuthLoader'

export function AuthWalletConnected({ setStep, type, onBack }: { onBack?: () => void; setStep: (step: AuthStep) => void; type?: 'add' }) {
  const router = useRouter()
  const setEmail = useOtpStore((state) => state.setEmail)
  const userId = useAuthStore((state) => state.userId)
  const { refetch: fetchProfile } = useQuery(createUserProfileQueryOptions(false))
  const { refetch: fetchBalances } = useQuery(createBalancesQueryOptions(false))
  const { selectedWalletType, selectedPublicKey } = useAuthStore(useShallow((state) => ({
    selectedWalletType: state.selectedWalletType,
    selectedPublicKey: state.selectedPublicKey,
  })))

  const wallet = useAppWallet(selectedWalletType)

  const [loading, setLoading] = useState<boolean>(false)

  const { call: authorize, isLoading } = useLogin({
    walletType: selectedWalletType,
    callback: () => { setStep(AuthStep.WALLET_CONFIRMED) },
    totpCallback: () => { setStep(AuthStep.TOTP) },
    passKeyCallback: () => { setStep(AuthStep.PASSKEY) },
    dappCallback: async () => {
      try {
        const res = await authService.pubEmailVerificaion(userId)
        setEmail(res.maskedEmail)
        router.push(`${AppRoutes.PUB_EMAIL_CODE}`)
      } catch (error) {
        if (error instanceof AxiosError) {
          message.error(error.response.data.message)
        }
      }
    },
  })

  const handleConfirm = useCallback(async () => {
    trackUserAction('User clicked Confirm on wallet-connected step', { wallet: selectedWalletType })

    if (type && type === 'add') {
      try {
        setLoading(true)
        const status = await profileService.linkPublicKey(wallet.publicKey, wallet.signMessage, wallet.confirmType, wallet.signTransaction)
        if (status === 200) {
          await fetchProfile()
          await fetchBalances()
          message.success({ content: 'Wallet successfully added', icon: '🎉 ' })
          router.push(AppRoutes.WALLETS_PAGE)
        }
      } catch (e) {
        Sentry.captureException(e)
        parseError(e)
      } finally {
        setLoading(false)
      }
      return
    }

    if (wallet.connected && wallet.publicKey && (wallet.signTransaction || wallet.signMessage)) {
      await authorize()
    }
  }, [authorize, fetchBalances, fetchProfile, router, selectedWalletType, type, wallet])

  useEffect(() => {
    if (wallet.publicKey && wallet.publicKey !== selectedPublicKey) {
      setStep(AuthStep.WALLETS)
      message.info('Public key changed. Reconnect wallet')
    }
  }, [wallet.publicKey, selectedPublicKey, setStep])

  if (!selectedPublicKey) return <AuthLoader />

  return (
    <Flex vertical justify="center" align="center" className="mobileContainer">
      <Flex className="w-full" flex={1} gap={15} justify="center" vertical>
        <span className={styles.icon}>👍</span>
        <AppTitle text="Connected" centered />
        <p className={styles.text}>
          Wallet
          {' '}
          <strong>{formatSolanaPublicKey(selectedPublicKey, 4)}</strong>
          {' '}
          successfully
          <br />
          connected to GoDefi
        </p>
        <p className={styles.text}>
          Now you need to confirm that you are
          {' '}
          <br />
          ready to use our application
        </p>
      </Flex>
      <Flex vertical gap={10} className="mt-auto w-full">
        <Button
          loading={isLoading || loading}
          onClick={handleConfirm}
          type="primary"
          className="mobilePrimaryButton"
        >
          Confirm
        </Button>
        {onBack && (
          <Button onClick={onBack} size="large" className="mobileSecondaryButton">
            Back
          </Button>
        )}
      </Flex>
    </Flex>
  )
}
